import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import ProtocolsAPI from '../../api/protocols';
import ProtocolCategoriesAPI from '../../api/protocolCategories';

export const state = {
  records: [],
  meta: { totalCount: 0, currentPage: 1, totalPages: 1 },
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isFetchingSteps: false,
    isFetchingMessages: false,
    isSendingMessage: false,
    isFetchingFiles: false,
    isUploadingFile: false,
    isFetchingCategories: false,
  },
  // Categories
  categories: [],
  // Detail data (per protocol)
  currentSteps: [],
  currentMessages: [],
  currentFiles: [],
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getProtocols(_state) {
    return _state.records;
  },
  getMeta(_state) {
    return _state.meta;
  },
  getProtocolById: _state => id => {
    return _state.records.find(p => p.id === Number(id));
  },
  getCategories(_state) {
    return _state.categories;
  },
  getCurrentSteps(_state) {
    return _state.currentSteps;
  },
  getCurrentMessages(_state) {
    return _state.currentMessages;
  },
  getCurrentFiles(_state) {
    return _state.currentFiles;
  },
};

export const actions = {
  // --- Protocols CRUD ---
  get: async ({ commit }, params = {}) => {
    commit(types.SET_PROTOCOLS_UI_FLAG, { isFetching: true });
    try {
      const response = await ProtocolsAPI.get(params);
      const { payload, meta } = response.data;
      commit(types.SET_PROTOCOLS, payload);
      commit(types.SET_PROTOCOLS_META, meta);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_PROTOCOLS_UI_FLAG, { isFetching: false });
    }
  },
  create: async ({ commit }, protocolObj) => {
    commit(types.SET_PROTOCOLS_UI_FLAG, { isCreating: true });
    try {
      const response = await ProtocolsAPI.create(protocolObj);
      commit(types.ADD_PROTOCOL, response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_PROTOCOLS_UI_FLAG, { isCreating: false });
    }
  },
  update: async ({ commit }, { id, ...updateObj }) => {
    commit(types.SET_PROTOCOLS_UI_FLAG, { isUpdating: true });
    try {
      const response = await ProtocolsAPI.update(id, updateObj);
      commit(types.EDIT_PROTOCOL, response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_PROTOCOLS_UI_FLAG, { isUpdating: false });
    }
  },
  delete: async ({ commit }, id) => {
    commit(types.SET_PROTOCOLS_UI_FLAG, { isDeleting: true });
    try {
      await ProtocolsAPI.delete(id);
      commit(types.DELETE_PROTOCOL, id);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_PROTOCOLS_UI_FLAG, { isDeleting: false });
    }
  },

  // --- Categories ---
  getCategories: async ({ commit }) => {
    commit(types.SET_PROTOCOLS_UI_FLAG, { isFetchingCategories: true });
    try {
      const response = await ProtocolCategoriesAPI.get();
      commit(types.SET_PROTOCOL_CATEGORIES, response.data);
    } catch (error) {
      // Ignore
    } finally {
      commit(types.SET_PROTOCOLS_UI_FLAG, { isFetchingCategories: false });
    }
  },
  createCategory: async ({ commit }, data) => {
    const response = await ProtocolCategoriesAPI.create(data);
    commit(types.ADD_PROTOCOL_CATEGORY, response.data);
    return response.data;
  },
  updateCategory: async ({ commit }, { id, ...data }) => {
    const response = await ProtocolCategoriesAPI.update(id, data);
    commit(types.EDIT_PROTOCOL_CATEGORY, response.data);
    return response.data;
  },
  deleteCategory: async ({ commit }, id) => {
    await ProtocolCategoriesAPI.delete(id);
    commit(types.DELETE_PROTOCOL_CATEGORY, id);
  },

  // --- Steps ---
  getSteps: async ({ commit }, protocolId) => {
    commit(types.SET_PROTOCOLS_UI_FLAG, { isFetchingSteps: true });
    try {
      const response = await ProtocolsAPI.getSteps(protocolId);
      commit(types.SET_PROTOCOL_STEPS, response.data);
    } catch (error) {
      // Ignore
    } finally {
      commit(types.SET_PROTOCOLS_UI_FLAG, { isFetchingSteps: false });
    }
  },
  toggleStep: async ({ commit, state: s }, { protocolId, stepId, status }) => {
    const response = await ProtocolsAPI.updateStep(protocolId, stepId, {
      status,
    });
    const updatedSteps = s.currentSteps.map(step =>
      step.id === stepId ? response.data : step
    );
    commit(types.SET_PROTOCOL_STEPS, updatedSteps);
    return response.data;
  },

  // --- Messages ---
  getMessages: async ({ commit }, protocolId) => {
    commit(types.SET_PROTOCOLS_UI_FLAG, { isFetchingMessages: true });
    try {
      const response = await ProtocolsAPI.getMessages(protocolId);
      commit(types.SET_PROTOCOL_MESSAGES, response.data);
    } catch (error) {
      // Ignore
    } finally {
      commit(types.SET_PROTOCOLS_UI_FLAG, { isFetchingMessages: false });
    }
  },
  sendMessage: async ({ commit }, { protocolId, content }) => {
    commit(types.SET_PROTOCOLS_UI_FLAG, { isSendingMessage: true });
    try {
      const response = await ProtocolsAPI.createMessage(protocolId, {
        protocol_message: { content },
      });
      commit(types.ADD_PROTOCOL_MESSAGE, response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_PROTOCOLS_UI_FLAG, { isSendingMessage: false });
    }
  },

  // --- Files ---
  getFiles: async ({ commit }, protocolId) => {
    commit(types.SET_PROTOCOLS_UI_FLAG, { isFetchingFiles: true });
    try {
      const response = await ProtocolsAPI.getFiles(protocolId);
      commit(types.SET_PROTOCOL_FILES, response.data);
    } catch (error) {
      // Ignore
    } finally {
      commit(types.SET_PROTOCOLS_UI_FLAG, { isFetchingFiles: false });
    }
  },
  uploadFile: async ({ dispatch }, { protocolId, formData }) => {
    await ProtocolsAPI.uploadFile(protocolId, formData);
    dispatch('getFiles', protocolId);
  },
  deleteFile: async ({ dispatch }, { protocolId, fileId }) => {
    await ProtocolsAPI.deleteFile(protocolId, fileId);
    dispatch('getFiles', protocolId);
  },

  // --- Transfer ---
  transfer: async ({ commit }, { protocolId, teamId, userId }) => {
    const response = await ProtocolsAPI.transfer(protocolId, {
      protocol: { team_id: teamId, user_id: userId },
    });
    commit(types.EDIT_PROTOCOL, response.data);
    return response.data;
  },
};

export const mutations = {
  [types.SET_PROTOCOLS_UI_FLAG](_state, data) {
    _state.uiFlags = { ..._state.uiFlags, ...data };
  },
  [types.SET_PROTOCOLS](_state, data) {
    _state.records = data;
  },
  [types.SET_PROTOCOLS_META](_state, data) {
    if (data) {
      _state.meta = {
        totalCount: data.total_count,
        currentPage: data.current_page,
        totalPages: data.total_pages,
      };
    }
  },
  [types.ADD_PROTOCOL]: MutationHelpers.create,
  [types.EDIT_PROTOCOL]: MutationHelpers.update,
  [types.DELETE_PROTOCOL]: MutationHelpers.destroy,

  // Categories
  [types.SET_PROTOCOL_CATEGORIES](_state, data) {
    _state.categories = data;
  },
  [types.ADD_PROTOCOL_CATEGORY](_state, data) {
    _state.categories.push(data);
  },
  [types.EDIT_PROTOCOL_CATEGORY](_state, data) {
    const idx = _state.categories.findIndex(c => c.id === data.id);
    if (idx !== -1) _state.categories.splice(idx, 1, data);
  },
  [types.DELETE_PROTOCOL_CATEGORY](_state, id) {
    _state.categories = _state.categories.filter(c => c.id !== id);
  },

  // Detail
  [types.SET_PROTOCOL_STEPS](_state, data) {
    _state.currentSteps = data;
  },
  [types.SET_PROTOCOL_MESSAGES](_state, data) {
    _state.currentMessages = data;
  },
  [types.ADD_PROTOCOL_MESSAGE](_state, data) {
    _state.currentMessages.push(data);
  },
  [types.SET_PROTOCOL_FILES](_state, data) {
    _state.currentFiles = data;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
